import { SignupSkipButton } from "../SignupSkipButton/SignupSkipButton";
import { SignupStepButton } from "../SignupStepButton/SignupStepButton";
import { SignupStepPrimaryButton } from "../SignupStepPrimaryButton/SignupStepPrimaryButton";
import { SignupErrorFeedback } from "../SignupErrorFeedback/SignupErrorFeedback";
import { FormikProps } from "formik";
import { WalletTypeStepFormType } from "../WalletTypeStepSlide/WalletTypeStepSlide";
import { Loader } from "@/components/Common/Loader/Loader";
import { ucfirst } from "@/utils/string";
import { useEffect, useRef } from "react";
import { AptosIcon } from "@/components/Icons/AptosIcon";
import env from "@/env";
import { SolanaIcon } from "@/components/Icons/SolanaIcon";
import { trackEvent } from "@/utils/analytics";
import { AnalyticsEventType } from "@/constants/analytics";

type WalletTypeStepSlideFormProps = FormikProps<WalletTypeStepFormType> & {
  onPrev?: () => void;
};

export const WalletTypeStepSlideForm: React.FC<WalletTypeStepSlideFormProps> = ({
  values,
  setFieldValue,
  isValid,
  isSubmitting,
  handleSubmit,
  errors,
  onPrev
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const { walletType } = values;
    if (!walletType) {
      return;
    }

    buttonRef.current?.click();
  }, [values]);

  return (
    <form className="flex flex-1 flex-col items-center justify-center" onSubmit={handleSubmit}>
      <div className="flex w-[380px] flex-col gap-5 text-center">
        <div className="flex flex-col gap-2">
          <h2 className="title-h4 text-white">Connect Your Wallet</h2>
          <div className="flex flex-col gap-2">
            <p className="subtext-x-small text-gray-dark-1000">
              This wallet will be used to send your $IO emission rewards
            </p>
          </div>
        </div>
        {errors.submit && <SignupErrorFeedback>{ucfirst(errors.submit)}</SignupErrorFeedback>}
        <div className="flex flex-col gap-2">
          {WALLET_OPTIONS.map((option) => {
            const { value, label } = option;
            const active = values.walletType === value;

            return (
              <SignupStepPrimaryButton
                key={value}
                active={active}
                onClick={() => {
                  setFieldValue("walletType", value);
                }}
              >
                <option.icon className="h-[20px]" />
                <span>Connect {label} Wallet</span>
              </SignupStepPrimaryButton>
            );
          })}
        </div>
        <div className="flex flex-row items-center gap-5">
          {onPrev && (
            <SignupStepButton
              className="flex-1"
              onClick={() => {
                trackEvent(AnalyticsEventType.BACK_CLICKED);
                onPrev();
              }}
            >
              Go back
            </SignupStepButton>
          )}
          <SignupStepButton
            ref={buttonRef}
            className="flex-1"
            type="submit"
            disabled={!isValid || isSubmitting}
          >
            <span>Next Step</span>
            {isSubmitting && <Loader className="!h-[14px] !w-[14px]" color="#fff" />}
          </SignupStepButton>
        </div>
        <div className="flex flex-row justify-center">
          <SignupSkipButton disabled={isSubmitting} />
        </div>
      </div>
    </form>
  );
};

const WALLET_OPTIONS = [
  ...(env.FEATUREFLAG_SOLANA_UPDATE_ENABLED
    ? [
        {
          value: "solana",
          label: "Solana",
          icon: SolanaIcon,
          scanLink: "https://solscan.io/account/"
        }
      ]
    : []),
  {
    value: "aptos",
    label: "Aptos",
    icon: AptosIcon,
    scanLink: "https://aptoscan.com/account/"
  }
] as const;
